import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { toast } from "react-toastify";

import { RubbleTicket } from "../../interface/api/RubbleTicket";
import { Truck } from "../../interface/api/Truck";
import { Project } from "../../interface/api/Project";
import { ContractorCompany } from "../../interface/api/ContractorCompany";
import { ContractorCompaniesWithThresholdsResponse } from "../../interface/api/ContractorCompaniesWithThresholdsResponse";
import contractorCompanyService from "../../services/contractorCompanyService";
import contractorProjectService from "../../services/contractorProjectService";
import truckService from "../../services/truckService";
import LicensePlateAutoLookup from "../shared/LicensePlateAutoLookup";
import { fillBlankTruckFields } from "../../utils/ticket-helpers";
import { useTruckNumberAutofill } from "../../hooks";
import FormSelectSearchField from "../shared/form-select-search-field";
import JobSiteField from "../shared/job-site-field";
import Button from "../shared/Button";
import PriceField from "../shared/price-field";
import { useDialogStyles } from "../shared/dialog/styles";
import { PaymentMethods, DefaultSalePrices, DefaultPaymentMethod } from "../../utils/constants";
import { SoilFLOIcon } from "../shared/icons";
import { useSoilFLOStyles } from "../../utils/soilflo-styles";
import { RubbleReceiveDialogProps } from "../../interface/dialog/RubbleReceiveDialogProps";
import FormTruckLicensePlateField from "../shared/form-truck-license-plate-field";

const ReceiveTicketDialog = ({
  open,
  ticket,
  onClose,
  onReceive,
}: RubbleReceiveDialogProps) => {
  const classes = useSoilFLOStyles();
  const dialogClasses = useDialogStyles();
  const [editedTicket, setEditedTicket] = useState<RubbleTicket | null>(
    null
  );
  const [contractors, setContractors] = useState<
    { value: number; label: string }[]
  >([]);
  const [miscContractor, setMiscContractor] = useState<ContractorCompany | null>(null);
  const [isMisc, setIsMisc] = useState(false);
  const prevContractorRef = useRef<{
    contractorCompanyId: number | null;
    contractorCompanyName: string | null;
    projectId: number | null;
    projectProjectId: string | null;
    jobSite: string;
  } | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [formAttempted, setFormAttempted] = useState(false);
  const [allTrucks, setAllTrucks] = useState<Truck[]>([]);

  const isSoilFLO = editedTicket?.isSoilFLOLoad === true;

  useEffect(() => {
    if (ticket) {
      setEditedTicket({
        ...ticket,
        salePrice: ticket.salePrice ?? DefaultSalePrices.Rubble,
        paymentMethod: ticket.paymentMethod ?? DefaultPaymentMethod,
      });
      setFormAttempted(false);
    }
  }, [ticket]);

  useEffect(() => {
    if (ticket && miscContractor) {
      // Default Rubble receive to the Miscellaneous company so the operator can
      // type the contractor by hand. Respect an existing contractor if set.
      if (!ticket.isSoilFLOLoad && !ticket.contractorCompanyId) {
        setIsMisc(true);
        setEditedTicket((prev) =>
          prev
            ? {
                ...prev,
                contractorCompanyId: miscContractor.id,
                contractorCompanyName: "",
              }
            : prev
        );
      } else {
        setIsMisc(ticket.contractorCompanyId === miscContractor.id);
      }
    }
  }, [ticket, miscContractor]);

  useEffect(() => {
    contractorCompanyService.getContractorCompanies().then((response) => {
      if (response.ok && response.data) {
        const data =
          response.data as ContractorCompaniesWithThresholdsResponse;
        const companies = data.contractorCompanies.filter(
          (c) => c.isRubble
        );
        const misc = companies.find((c) => c.isMiscellaneous) ?? null;
        setMiscContractor(misc);
        setContractors(
          companies.filter((c) => !c.isMiscellaneous).map((c) => ({ value: c.id, label: c.name }))
        );
      }
    });
    truckService
      .getTrucks({ transportCompanyId: null, truck: null, transportContact: null })
      .then((response) => {
        if (response.ok && response.data) {
          setAllTrucks(response.data as Truck[]);
        }
      });
  }, []);

  // Load projects when contractor changes
  useEffect(() => {
    if (editedTicket?.contractorCompanyId) {
      contractorProjectService
        .getContractorProjects(editedTicket.contractorCompanyId)
        .then((response) => {
          if (response.ok) {
            setProjects(response.data as Project[]);
          }
        });
    } else {
      setProjects([]);
    }
  }, [editedTicket?.contractorCompanyId]);

  // Hauler + truck # fills the plate and driver from the registered truck.
  useTruckNumberAutofill(
    editedTicket?.haulageCompanyName,
    editedTicket?.truckNumber,
    (truck) =>
      setEditedTicket((prev) => {
        if (!prev) return prev;
        const filled = fillBlankTruckFields(truck, {
          licensePlate: prev.truckLicensePlate,
          driverName: prev.truckDriverName,
        });
        return { ...prev, truckLicensePlate: filled.licensePlate, truckDriverName: filled.driverName };
      }),
    !isSoilFLO
  );

  if (!editedTicket) return null;

  const updateField = (field: keyof RubbleTicket, value: any) => {
    setEditedTicket({ ...editedTicket, [field]: value });
  };

  const isEmpty = (val: any) => val === null || val === undefined || val === "";

  const onSubmit = () => {
    setFormAttempted(true);
    const contractorMissing = isSoilFLO
      ? false
      : isMisc
        ? isEmpty(editedTicket.contractorCompanyName)
        : isEmpty(editedTicket.contractorCompanyId);
    const missing =
      contractorMissing ||
      isEmpty(editedTicket.paymentMethod) ||
      isEmpty(editedTicket.salePrice);
    if (missing) {
      toast.error("Please fill in all required fields");
      return;
    }
    onReceive(editedTicket);
  };

  const sfClass = isSoilFLO ? classes.soilFLOField : undefined;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className={dialogClasses.dialogTitle}>
        Receive Rubble Ticket #{editedTicket.ticketNumber}
      </DialogTitle>
      <DialogContent className={dialogClasses.dialogContent}>
        {isSoilFLO && (
          <Box display="flex" alignItems="center" mb={2} style={{ gap: 8 }}>
            <SoilFLOIcon />
            <Typography variant="body2" color="textSecondary">
              Fields pulled from SoilFLO are read-only
            </Typography>
          </Box>
        )}
        <LicensePlateAutoLookup
          ticket={editedTicket}
          setTicket={(t) => setEditedTicket(t)}
          trucks={allTrucks}
        />
        <Grid container spacing={2}>
          {!isSoilFLO && miscContractor && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isMisc}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsMisc(checked);
                      if (checked) {
                        prevContractorRef.current = {
                          contractorCompanyId: editedTicket.contractorCompanyId,
                          contractorCompanyName: editedTicket.contractorCompanyName,
                          projectId: editedTicket.projectId,
                          projectProjectId: editedTicket.projectProjectId,
                          jobSite: editedTicket.jobSite ?? "",
                        };
                        setEditedTicket({
                          ...editedTicket,
                          contractorCompanyId: miscContractor.id,
                          contractorCompanyName: "",
                          projectId: null,
                          projectProjectId: null,
                          jobSite: "",
                        });
                      } else {
                        const prev = prevContractorRef.current;
                        setEditedTicket({
                          ...editedTicket,
                          contractorCompanyId: prev?.contractorCompanyId ?? null,
                          contractorCompanyName: prev?.contractorCompanyName ?? null,
                          projectId: prev?.projectId ?? null,
                          projectProjectId: prev?.projectProjectId ?? null,
                          jobSite: prev?.jobSite ?? "",
                        });
                        prevContractorRef.current = null;
                      }
                    }}
                    color="primary"
                  />
                }
                label="Miscellaneous Company"
              />
            </Grid>
          )}
          <Grid item xs={12}>
            {isSoilFLO ? (
              <TextField
                fullWidth
                variant="outlined"
                label="Contractor"
                value={editedTicket.contractorCompanyName ?? ""}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                className={classes.soilFLOField}
              />
            ) : isMisc ? (
              <>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Company Name"
                  required
                  error={formAttempted && isEmpty(editedTicket.contractorCompanyName)}
                  helperText={formAttempted && isEmpty(editedTicket.contractorCompanyName) ? "Required" : ""}
                  value={editedTicket.contractorCompanyName ?? ""}
                  onChange={(e) => updateField("contractorCompanyName", e.target.value)}
                />
              </>
            ) : (
              <>
                <FormSelectSearchField
                  name="contractorCompanyId"
                  placeholder="Contractor Company *"
                  options={contractors}
                  value={
                    editedTicket.contractorCompanyId
                      ? contractors.find(
                          (c) => c.value === editedTicket.contractorCompanyId
                        ) ?? null
                      : null
                  }
                  onChange={(option: any) => {
                    if (option) {
                      setEditedTicket({
                        ...editedTicket,
                        contractorCompanyId: option.value,
                        contractorCompanyName: option.label,
                        projectId: null,
                        projectProjectId: null,
                        jobSite: "",
                      });
                    } else {
                      setEditedTicket({
                        ...editedTicket,
                        contractorCompanyId: null,
                        contractorCompanyName: null,
                        projectId: null,
                        projectProjectId: null,
                        jobSite: "",
                      });
                    }
                  }}
                  isClearable
                />
                {formAttempted && isEmpty(editedTicket.contractorCompanyId) && (
                  <Typography variant="caption" color="error">Required</Typography>
                )}
              </>
            )}
          </Grid>
          {!isMisc && (
          <>
          <Grid item xs={12}>
            {isSoilFLO ? (
              <TextField
                fullWidth
                variant="outlined"
                label="Project"
                value={
                  editedTicket.projectId
                    ? `${editedTicket.projectProjectId ?? ""} — ${editedTicket.jobSite ?? ""}`
                    : ""
                }
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                className={classes.soilFLOField}
              />
            ) : (
              <FormSelectSearchField
                name="projectId"
                placeholder="Project"
                options={projects.map((p) => ({
                  value: p.id,
                  label: `${p.projectId} — ${p.jobSite}`,
                }))}
                value={
                  editedTicket.projectId
                    ? projects
                        .map((p) => ({
                          value: p.id,
                          label: `${p.projectId} — ${p.jobSite}`,
                        }))
                        .find((opt) => opt.value === editedTicket.projectId) ?? null
                    : null
                }
                onChange={(option: any) => {
                  if (option) {
                    const project = projects.find((p) => p.id === option.value);
                    setEditedTicket({
                      ...editedTicket,
                      projectId: option.value,
                      projectProjectId: project?.projectId ?? null,
                      jobSite: project?.jobSite ?? editedTicket.jobSite,
                      salePrice:
                        project?.salePrice != null
                          ? project.salePrice
                          : editedTicket.salePrice,
                    });
                  } else {
                    setEditedTicket({
                      ...editedTicket,
                      projectId: null,
                      projectProjectId: null,
                      jobSite: "",
                    });
                  }
                }}
                isClearable
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {isSoilFLO ? (
              <TextField
                fullWidth
                variant="outlined"
                label="Job Site"
                value={editedTicket.jobSite ?? ""}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                className={classes.soilFLOField}
              />
            ) : (
              <JobSiteField
                label="Job Site"
                value={editedTicket.jobSite ?? ""}
                items={projects.map((p) => p.jobSite)}
                onChange={(val) => updateField("jobSite", val)}
              />
            )}
          </Grid>
          </>
          )}
          <Grid item xs={12} sm={6}>
            {isSoilFLO ? (
              <TextField
                fullWidth
                variant="outlined"
                label="Truck License Plate"
                value={editedTicket.truckLicensePlate?.toUpperCase() ?? ""}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                className={classes.soilFLOField}
              />
            ) : (
              <FormTruckLicensePlateField
                value={editedTicket.truckLicensePlate ?? ""}
                name="truckLicensePlate"
                label="Truck License Plate"
                onChange={(e) =>
                  updateField("truckLicensePlate", e.target.value.toUpperCase())
                }
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="Truck Number"
              value={editedTicket.truckNumber ?? ""}
              onChange={(e) => !isSoilFLO && updateField("truckNumber", e.target.value)}
              InputProps={{ readOnly: isSoilFLO }}
              className={sfClass}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              label="Driver Name"
              value={editedTicket.truckDriverName ?? ""}
              onChange={(e) =>
                !isSoilFLO && updateField("truckDriverName", e.target.value)
              }
              InputProps={{ readOnly: isSoilFLO }}
              className={sfClass}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              label="Haulage Company"
              value={editedTicket.haulageCompanyName ?? ""}
              onChange={(e) =>
                !isSoilFLO && updateField("haulageCompanyName", e.target.value)
              }
              InputProps={{ readOnly: isSoilFLO }}
              className={sfClass}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" required error={formAttempted && isEmpty(editedTicket.paymentMethod)}>
              <InputLabel>Payment Method</InputLabel>
              <Select
                label="Payment Method"
                value={editedTicket.paymentMethod ?? ""}
                onChange={(e) =>
                  updateField("paymentMethod", e.target.value)
                }
              >
                <MenuItem value="">None</MenuItem>
                {PaymentMethods.map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <PriceField
              label="Sale Price *"
              value={editedTicket.salePrice}
              onChange={(val) => updateField("salePrice", val)}
            />
            {formAttempted && isEmpty(editedTicket.salePrice) && (
              <Typography variant="caption" color="error">Required</Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              label="Notes"
              value={editedTicket.notes ?? ""}
              onChange={(e) => updateField("notes", e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={dialogClasses.dialogActions}>
        <div className={dialogClasses.actionButton}>
          <Button
            label="Cancel"
            onClick={onClose}
            color="default"
            variant="contained"
            type="button"
          />
        </div>
        <div className={dialogClasses.actionButton}>
          <Button
            label="Receive Ticket"
            onClick={onSubmit}
            color="primary"
            variant="contained"
            type="button"
          />
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default ReceiveTicketDialog;
